import {
  Button,
  closeCurrentDialog,
  DataStatus,
  Form,
  FormButton,
  FormField,
  FormSubmitHandler,
} from 'platform/components';
import {HStack, VStack} from 'platform/foundation';
import {z} from 'zod';

import {useGetDeliveryNoteItemRemovalReasonQuery} from '@dms/api/warehouse';
import i18n from '@dms/i18n';

import {Nullish, RequiredTestIdProps, suffixTestId} from 'shared/web';

const RemovalReasonFormSchema = z.object({
  removalReason: z.string().min(1),
});

type RemovalReasonFormType = z.infer<typeof RemovalReasonFormSchema>;

export interface RemovalReasonProps extends RequiredTestIdProps {
  receiveNoteItemId: string | Nullish;
  onRemove: (receiveNoteItemId: string, removalReason: string) => void;
  onDiscard: VoidFunction;
}

export function RemovalReason(props: RemovalReasonProps) {
  const {data: codelist, isLoading, isError} = useGetDeliveryNoteItemRemovalReasonQuery();

  const removalReasonOptions =
    codelist?.map((code) => ({
      value: code.code,
      label: code.name,
    })) ?? [];

  const defaultValues: Partial<RemovalReasonFormType> = {
    removalReason: removalReasonOptions[0]?.value,
  };

  const handleSubmit: FormSubmitHandler<RemovalReasonFormType> = async (formValues) => {
    if (props.receiveNoteItemId) {
      props.onRemove(props.receiveNoteItemId, formValues.removalReason);
    }
    await closeCurrentDialog();
  };

  const handleDiscard = () => {
    props.onDiscard();
  };

  return (
    <DataStatus isLoading={isLoading} isError={isError}>
      <Form<RemovalReasonFormType>
        experimentalZodSchema={RemovalReasonFormSchema}
        onSubmit={handleSubmit}
        defaultValues={defaultValues}
      >
        {(control) => (
          <>
            <VStack spacing={4}>
              <FormField
                isRequired
                isNotClearable
                control={control}
                type="choice"
                name="removalReason"
                label={i18n.t('entity.warehouse.labels.removalReason')}
                options={removalReasonOptions}
                menuInPortal
                data-testid={suffixTestId('inputs.removalReason', props)}
              />

              <HStack spacing={2} justify="flex-end">
                <Button
                  title={i18n.t('general.actions.discard')}
                  variant="secondary"
                  onClick={handleDiscard}
                  data-testid={suffixTestId('actions.discard', props)}
                />
                <FormButton
                  control={control}
                  type="submit"
                  title={i18n.t('general.actions.remove')}
                  variant="primary"
                  data-testid={suffixTestId('actions.remove', props)}
                />
              </HStack>
            </VStack>
          </>
        )}
      </Form>
    </DataStatus>
  );
}
